import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Box,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
  Stack,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FlightTakeoff as FlightIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../context/authContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfile } from '../../context/userContext';
import AddDroneDialog from '../../components/fleet/AddDroneDialog';
import EditDroneDialog from '../../components/fleet/EditDroneDialog';
import LogFlightDialog from '../../components/fleet/LogFlightDialog';
import MaintenanceLogList from '../../components/fleet/MaintenanceLogList';
import { batchGetPresignedViewUrls } from '../../utils/fileUtils';
import { useToast } from '../../hooks/useToast';
import { addFlightLog } from '../../network/apis/flightLog.api';
import styles from '../../styles/DroneInventory.module.css';

const DroneInventory = () => {
  const { currentOrganization, userRole, organizationMembers } = useOrganization();
  const { token } = useAuth();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfile();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [drones, setDrones] = useState([]);
  const [filteredDrones, setFilteredDrones] = useState([]);
  const [imageUrls, setImageUrls] = useState({}); // Map of drone.imageUrl to presigned URL
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [logFlightDialogOpen, setLogFlightDialogOpen] = useState(false);
  const [selectedDrone, setSelectedDrone] = useState(null);

  // Permission checks
  const canManageDrones = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());
  const canDeleteDrones = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // Fetch drones from backend
  useEffect(() => {
    if (currentOrganization?.organizationId) {
      fetchDrones();
    }
  }, [currentOrganization]);

  const fetchDrones = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch drones');
      }

      const data = await response.json();
      const fetchedDrones = data.drones || [];
      setDrones(fetchedDrones);
      setFilteredDrones(fetchedDrones);

      // Fetch presigned URLs for all drone images
      const imageUrlsToFetch = fetchedDrones
        .filter(drone => drone.imageUrl)
        .map(drone => drone.imageUrl);

      if (imageUrlsToFetch.length > 0) {
        const presignedUrls = await batchGetPresignedViewUrls(
          imageUrlsToFetch,
          currentOrganization?.organizationUuid,
          getAccessTokenSilently
        );
        setImageUrls(presignedUrls);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredDrones(drones);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = drones.filter(
        (drone) =>
          drone.manufacturer?.toLowerCase().includes(query) ||
          drone.model?.toLowerCase().includes(query) ||
          drone.serialNumber?.toLowerCase().includes(query) ||
          drone.registrationNumber?.toLowerCase().includes(query) ||
          drone.nickname?.toLowerCase().includes(query) ||
          drone.status?.toLowerCase().includes(query)
      );
      setFilteredDrones(filtered);
    }
  }, [searchQuery, drones]);

  const handleAddDrone = async (droneData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(droneData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add drone');
      }

      // Refresh drone list
      await fetchDrones();
      setAddDialogOpen(false);
      showSuccess('Drone added successfully!');
    } catch (err) {
      showError(`Failed to add drone: ${err.message}`);
      throw err;
    }
  };

  const handleEditDrone = async (droneData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones/${selectedDrone.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(droneData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update drone');
      }

      // Refresh drone list
      await fetchDrones();
      setEditDialogOpen(false);
      setSelectedDrone(null);
      showSuccess('Drone updated successfully!');
    } catch (err) {
      showError(`Failed to update drone: ${err.message}`);
      throw err;
    }
  };

  const handleDeleteDrone = async (droneId) => {
    if (!window.confirm('Are you sure you want to delete this drone? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones/${droneId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete drone');
      }

      // Refresh drone list
      await fetchDrones();
      showSuccess('Drone deleted successfully!');
    } catch (err) {
      showError(`Failed to delete drone: ${err.message}`);
    }
  };

  const handleLogFlight = async (flightData) => {
    try {
      await addFlightLog(token, currentOrganization.organizationUuid, flightData);

      // Refresh drone list to show updated flight hours
      await fetchDrones();
      showSuccess('Flight logged successfully!');
      setLogFlightDialogOpen(false);
    } catch (err) {
      throw err; // Re-throw to let dialog handle the error display
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'retired':
        return 'default';
      case 'lost':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate statistics
  const maxDrones = currentOrganization?.subscription?.limits?.maxDrones || 0;
  const currentCount = drones.length;
  const activeCount = drones.filter(d => d.status === 'active').length;
  const maintenanceCount = drones.filter(d => d.status === 'maintenance').length;
  const atLimit = maxDrones > 0 && currentCount >= maxDrones;

  if (loading) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Typography>Loading drone inventory...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className={styles.container} sx={{ px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box className={styles.header}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Drone Inventory
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Manage your organization's drone fleet
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} className={styles.statsGrid}>
        <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Total Drones
              </Typography>
              <Typography variant="h3" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {currentCount} / {maxDrones}
              </Typography>
              {atLimit && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                  Limit reached
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Active
              </Typography>
              <Typography variant="h3" color="success.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Maintenance
              </Typography>
              <Typography variant="h3" color="warning.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {maintenanceCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Flight Hours
              </Typography>
              <Typography variant="h3" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {drones.reduce((sum, d) => sum + (d.totalFlightHours || 0), 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Message */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Controls */}
      <Box className={styles.controls} sx={{
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <TextField
          placeholder="Search drones..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          className={styles.searchField}
          fullWidth={isMobile}
        />
        {canManageDrones && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={atLimit}
            fullWidth={isMobile}
          >
            Add Drone
          </Button>
        )}
      </Box>

      {/* Drone List - Cards on mobile, Table on desktop */}
      {isMobile ? (
        // Mobile Card Layout
        filteredDrones.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery
                ? 'No drones found matching your search'
                : 'No drones in your fleet yet. Add your first drone to get started!'}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {filteredDrones.map((drone) => (
              <Card key={drone.id} variant="outlined">
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Compact Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {drone.nickname || drone.model}
                      </Typography>
                      {drone.nickname && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {drone.model}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={drone.status}
                      color={getStatusColor(drone.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize', height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>

                  {/* Compact Details - 3 columns */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, fontSize: '0.75rem' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        Manufacturer
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {drone.manufacturer}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        Flight Hours
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {(drone.totalFlightHours || 0).toFixed(2)} hrs
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        Total Flights
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                        {drone.totalFlights || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>

                {canManageDrones && (
                  <CardActions sx={{ p: 1, pt: 0, gap: 0.5, justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => {
                        setSelectedDrone(drone);
                        setLogFlightDialogOpen(true);
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <FlightIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedDrone(drone);
                        setMaintenanceDialogOpen(true);
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <BuildIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedDrone(drone);
                        setEditDialogOpen(true);
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {canDeleteDrones && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDrone(drone.id)}
                        sx={{ padding: '4px' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        )
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Nickname / Model</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Registration #</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Flight Hours</TableCell>
                <TableCell>Total Flights</TableCell>
                <TableCell>Last Maintenance</TableCell>
                {canManageDrones && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDrones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageDrones ? 10 : 9} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery
                        ? 'No drones found matching your search'
                        : 'No drones in your fleet yet. Add your first drone to get started!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrones.map((drone) => (
                <TableRow key={drone.id}>
                  <TableCell>
                    {drone.imageUrl ? (
                      <Box
                        component="img"
                        src={imageUrls[drone.imageUrl] || drone.imageUrl}
                        alt={drone.nickname || drone.model}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <FlightIcon sx={{ color: '#9e9e9e' }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {drone.nickname || drone.model}
                      </Typography>
                      {drone.nickname && (
                        <Typography variant="caption" color="text.secondary">
                          {drone.model}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{drone.manufacturer}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {drone.serialNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {drone.registrationNumber || (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={drone.status}
                      color={getStatusColor(drone.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize', borderRadius: '4px' }}
                    />
                  </TableCell>
                  <TableCell>{(drone.totalFlightHours || 0).toFixed(2)} hrs</TableCell>
                  <TableCell>{drone.totalFlights || 0}</TableCell>
                  <TableCell>{formatDate(drone.lastMaintenanceDate)}</TableCell>
                  {canManageDrones && (
                    <TableCell align="right">
                      <Tooltip title="Log Flight Hours">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedDrone(drone);
                            setLogFlightDialogOpen(true);
                          }}
                        >
                          <FlightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Maintenance History">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedDrone(drone);
                            setMaintenanceDialogOpen(true);
                          }}
                        >
                          <BuildIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Drone">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDrone(drone);
                            setEditDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDeleteDrones && (
                        <Tooltip title="Delete Drone">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDrone(drone.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Add Drone Dialog */}
      <AddDroneDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddDrone}
        organizationName={currentOrganization?.organizationName}
      />

      {/* Edit Drone Dialog */}
      {selectedDrone && (
        <EditDroneDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedDrone(null);
          }}
          onEdit={handleEditDrone}
          drone={selectedDrone}
        />
      )}

      {/* Log Flight Dialog */}
      {selectedDrone && (
        <LogFlightDialog
          open={logFlightDialogOpen}
          onClose={() => {
            setLogFlightDialogOpen(false);
            setSelectedDrone(null);
          }}
          onSubmit={handleLogFlight}
          drone={selectedDrone}
          pilots={(() => {
            // Show all organization members except viewers
            return (organizationMembers || []).filter(member =>
              member.role?.toLowerCase() !== 'viewer'
            );
          })()}
          currentUserId={profile?.id}
        />
      )}

      {/* Maintenance History Dialog */}
      {selectedDrone && (
        <Dialog
          open={maintenanceDialogOpen}
          onClose={() => {
            setMaintenanceDialogOpen(false);
            setSelectedDrone(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent sx={{ p: 3 }}>
            <MaintenanceLogList
              droneId={selectedDrone.id}
              organizationUuid={currentOrganization?.organizationUuid}
              droneName={`${selectedDrone.manufacturer} ${selectedDrone.model} ${selectedDrone.nickname ? `(${selectedDrone.nickname})` : ''}`}
            />
          </DialogContent>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setMaintenanceDialogOpen(false);
                setSelectedDrone(null);
              }}
            >
              Close
            </Button>
          </Box>
        </Dialog>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DroneInventory;
